import { useSearchParams } from 'react-router-dom';
import { Button } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../i18n/I18nProvider';
import { Banner } from '../../../components/panel';
import { LooseEndsList, useLooseEnds } from './LooseEndsList';

/** The query parameter that opens the drawer: the loose-ends notice links to `/flows?loose_ends=1`. */
export const LOOSE_ENDS_PARAM = 'loose_ends';

/**
 * The bar above the Flows pages when steps in switched-on flows lead nowhere (2 November 2026): how many, the ones
 * where chats stop said in bold, and Review, which opens the list in a drawer. The drawer's state lives in the address,
 * so the notice email's link opens it and Back from a step returns to it. Nothing shows while there are none, but
 * `?loose_ends=1` still opens the drawer, which then says every step leads somewhere.
 */
export function LooseEndsBar() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const open = params.get(LOOSE_ENDS_PARAM) === '1';
  const setOpen = (on: boolean) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (on) next.set(LOOSE_ENDS_PARAM, '1'); else next.delete(LOOSE_ENDS_PARAM);
    return next;
  }, { replace: true });
  const query = useLooseEnds();
  const items = query.data?.items ?? [];
  const breaking = items.filter((e) => e.breaks).length;

  return (
    <>
      {items.length > 0 && (
        <div data-testid="loose-ends-bar" data-breaking={breaking}>
          <Banner
            tone={breaking > 0 ? 'warning' : 'info'}
            action={<Button size="xs" variant="default" onClick={() => setOpen(true)} data-testid="loose-ends-review">{t('flows.looseEnds.review')}</Button>}
          >
            {items.length === 1 ? t('flows.looseEnds.barOne') : t('flows.looseEnds.bar', { count: items.length })}
            {breaking > 0 && <>{' '}<strong>{t('flows.looseEnds.barBreaking', { count: breaking })}</strong></>}
          </Banner>
        </div>
      )}
      <Drawer opened={open} onClose={() => setOpen(false)} title={t('flows.looseEnds.title')} position="right" size="lg">
        {/* Open the step leaves the drawer open in the address, so Back comes back to the list. */}
        <LooseEndsList withTitle={false} guide="flows-loose-ends" />
      </Drawer>
    </>
  );
}
